<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import WahaSessionsAPI from 'dashboard/api/wahaSessions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WahaToggleList from './WahaToggleList.vue';

// Pulls WhatsApp history into the inbox through the WAHA chatwoot app; the
// options are saved per inbox as a template for the next pull.
const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const DEFAULT_TEMPLATE = {
  end: '7d',
  start: '0d',
  chat: '',
  force: false,
  no_dm: false,
  groups: false,
  channels: false,
  status: false,
  broadcast: false,
  media: false,
  pause: false,
  resolve_conversations: false,
  batch: 100,
  progress: 1000,
  attempts: 6,
  timeout: '10m',
  timeout_media: '30s',
};
const FLAGS = [
  'force',
  'no_dm',
  'groups',
  'channels',
  'status',
  'broadcast',
  'media',
  'pause',
  'resolve_conversations',
];
const FLAG_KEYS = {
  force: 'FORCE',
  no_dm: 'NO_DM',
  groups: 'GROUPS',
  channels: 'CHANNELS',
  status: 'STATUS',
  broadcast: 'BROADCAST',
  media: 'MEDIA',
  pause: 'PAUSE',
  resolve_conversations: 'RESOLVE',
};
const UNITS = [
  ['d', 'DAYS'],
  ['h', 'HOURS'],
  ['m', 'MINUTES'],
  ['s', 'SECONDS'],
];
const PRESETS = [7, 30, 90];

const template = reactive({ ...DEFAULT_TEMPLATE });
const isRunning = ref(false);
const isSavingTemplate = ref(false);
const report = ref('');
const reportCheckedAt = ref('');

const applyTemplate = value => {
  Object.assign(template, DEFAULT_TEMPLATE, value || {});
};

watch(
  () => props.session,
  session => {
    applyTemplate(session.message_sync_template);
    report.value = session.message_sync_status_report || '';
    reportCheckedAt.value = session.message_sync_status_checked_at || '';
  },
  { immediate: true }
);

const unitOptions = computed(() =>
  UNITS.map(([value, key]) => ({
    value,
    label: t(`INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.DURATION_UNITS.${key}`),
  }))
);

// "7d" <-> amount 7 + unit d
const durationField = (field, fallback) => {
  const parse = () => {
    const match = `${template[field] || ''}`.trim().match(/^(\d+)([dhms])$/i);
    return match
      ? { amount: Number(match[1]), unit: match[2].toLowerCase() }
      : fallback;
  };
  return {
    amount: computed({
      get: () => parse().amount,
      set: value => {
        template[field] = `${Math.max(0, Number(value) || 0)}${parse().unit}`;
      },
    }),
    unit: computed({
      get: () => parse().unit,
      set: value => {
        template[field] = `${parse().amount}${value}`;
      },
    }),
  };
};

const durations = [
  ['end', 'END', { amount: 7, unit: 'd' }],
  ['start', 'START', { amount: 0, unit: 'd' }],
  ['timeout', 'TIMEOUT', { amount: 10, unit: 'm' }],
  ['timeout_media', 'TIMEOUT_MEDIA', { amount: 30, unit: 's' }],
].map(([field, key, fallback]) => ({
  field,
  key,
  ...durationField(field, fallback),
}));

const flagItems = computed(() =>
  FLAGS.map(key => ({
    key,
    label: t(`INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.${FLAG_KEYS[key]}_LABEL`),
    description: t(`INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.${FLAG_KEYS[key]}_DESC`),
  }))
);
const flags = computed({
  get: () => Object.fromEntries(FLAGS.map(key => [key, template[key]])),
  set: value => {
    FLAGS.forEach(key => {
      template[key] = Boolean(value[key]);
    });
  },
});

const reportCheckedAtLabel = computed(() =>
  reportCheckedAt.value
    ? new Date(reportCheckedAt.value).toLocaleString()
    : ''
);

const applyPreset = days => {
  template.end = `${days}d`;
  template.start = '0d';
};

const pull = async () => {
  isRunning.value = true;
  try {
    await WahaSessionsAPI.syncMessages(props.session.id, {
      command_action: 'pull',
      ...template,
    });
    useAlert(t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.ERROR')
    );
  } finally {
    isRunning.value = false;
  }
};

const reviewProgress = async () => {
  isRunning.value = true;
  try {
    const { data } = await WahaSessionsAPI.syncMessages(props.session.id, {
      command_action: 'status',
    });
    report.value =
      data.status_report ||
      (data.status_report_pending
        ? t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_PENDING')
        : '');
    reportCheckedAt.value =
      data.status_report_checked_at || new Date().toISOString();
    useAlert(t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_ERROR')
    );
  } finally {
    isRunning.value = false;
  }
};

const persistTemplate = async (value, successKey, errorKey) => {
  isSavingTemplate.value = true;
  try {
    const { data } = await WahaSessionsAPI.updateMessageSyncTemplate(
      props.session.id,
      value
    );
    applyTemplate(data.template);
    useAlert(t(successKey));
  } catch (error) {
    useAlert(parseAPIErrorResponse(error) || t(errorKey));
  } finally {
    isSavingTemplate.value = false;
  }
};

const saveTemplate = () =>
  persistTemplate(
    { ...template },
    'INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.TEMPLATE_SUCCESS',
    'INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.TEMPLATE_ERROR'
  );

const restoreDefaults = () => {
  applyTemplate(DEFAULT_TEMPLATE);
  return persistTemplate(
    DEFAULT_TEMPLATE,
    'INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.RESET_SUCCESS',
    'INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.RESET_ERROR'
  );
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.SUBTITLE') }}
    </p>

    <div class="flex flex-wrap gap-2">
      <NextButton
        v-for="days in PRESETS"
        :key="days"
        xs
        faded
        slate
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.PRESET', { days })"
        @click="applyPreset(days)"
      />
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <div v-for="duration in durations" :key="duration.field" class="grid gap-1">
        <span class="text-sm text-n-slate-12">
          {{ $t(`INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.${duration.key}_LABEL`) }}
        </span>
        <div class="grid grid-cols-[minmax(0,1fr)_9rem] gap-2">
          <Input v-model="duration.amount.value" type="number" min="0" />
          <NextSelect
            v-model="duration.unit.value"
            class="w-full"
            :options="unitOptions"
          />
        </div>
      </div>
      <Input
        v-model="template.chat"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.CHAT_LABEL')"
        :placeholder="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.CHAT_PLACEHOLDER')"
      />
      <Input
        v-model="template.batch"
        type="number"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.BATCH_LABEL')"
      />
      <Input
        v-model="template.progress"
        type="number"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.PROGRESS_LABEL')"
      />
      <Input
        v-model="template.attempts"
        type="number"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.ATTEMPTS_LABEL')"
      />
    </div>

    <WahaToggleList v-model="flags" :items="flagItems" />

    <div class="flex flex-wrap gap-2">
      <NextButton
        icon="i-lucide-history"
        :is-loading="isRunning"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.ACTION')"
        @click="pull"
      />
      <NextButton
        faded
        slate
        :is-loading="isRunning"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_ACTION')"
        @click="reviewProgress"
      />
      <NextButton
        faded
        slate
        :is-loading="isSavingTemplate"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.TEMPLATE_ACTION')"
        @click="saveTemplate"
      />
      <NextButton
        ghost
        slate
        :is-loading="isSavingTemplate"
        :label="$t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.RESET_ACTION')"
        @click="restoreDefaults"
      />
    </div>

    <div
      v-if="report || reportCheckedAtLabel"
      class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak p-4"
    >
      <div class="flex items-center justify-between gap-3 mb-2">
        <span class="text-sm text-n-slate-12">
          {{ $t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_REPORT_TITLE') }}
        </span>
        <span v-if="reportCheckedAtLabel" class="text-xs text-n-slate-10">
          {{
            $t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_REPORT_AT', {
              at: reportCheckedAtLabel,
            })
          }}
        </span>
      </div>
      <pre class="whitespace-pre-wrap break-words text-sm text-n-slate-12">{{
        report || $t('INBOX_MGMT.WAHA.APP.MESSAGE_SYNC.STATUS_EMPTY')
      }}</pre>
    </div>
  </div>
</template>
